import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAppStore } from './lib/store';
import { CategoryList } from './components/CategoryList';
import { LayoutCard } from './components/LayoutCard';
import { SourceCard } from './components/SourceCard';
import { CanvasPreview } from './components/CanvasPreview';
import { ICONS } from './components/Icon';
import { useToast } from './components/Toast';
import { TEMPLATES, type Template } from './lib/templates';
import { RESOLUTIONS } from './lib/obs-engine';
import { buildOBSProfile, copyToClipboard } from './lib/obs-export';
import type { Category, Scene, SceneSource } from './types/obs';
import { uuid } from './lib/obs-engine';

// Keep source positions/sizes as 0–1 percentages
function templateToSources(template: Template): SceneSource[] {
  return template.sources.map((s) => ({
    id: uuid(),
    type: s.type as SceneSource['type'],
    slot: s.slot,
    name: s.slot,
    position: { x: s.position.x, y: s.position.y },
    size: { w: s.size.w, h: s.size.h },
    order: s.order,
    visible: true,
    border: s.border ? { ...s.border } : undefined,
  }));
}

interface CollectionScene {
  name: string;
  sources: SceneSource[];
  vertical?: boolean;
}

function MainApp() {
  const { showToast, ToastComponent } = useToast();

  const selectedSceneId = useAppStore((s) => s.selectedSceneId);
  const setSelectedSceneId = useAppStore((s) => s.setSelectedSceneId);
  const selectedCategoryId = useAppStore((s) => s.selectedCategoryId);
  const setSelectedCategoryId = useAppStore((s) => s.setSelectedCategoryId);
  const searchQuery = useAppStore((s) => s.searchQuery);
  const setSearchQuery = useAppStore((s) => s.setSearchQuery);
  const resolution = useAppStore((s) => s.resolution);
  const setResolution = useAppStore((s) => s.setResolution);
  const isVertical = useAppStore((s) => s.isVertical);
  const setIsVertical = useAppStore((s) => s.setIsVertical);
  const adminToggles = useAppStore((s) => s.adminToggles);
  const loadAdminToggles = useAppStore((s) => s.loadAdminToggles);

  const [openSourceId, setOpenSourceId] = useState<string | null>(null);
  const [sources, setSources] = useState<SceneSource[]>([]);
  const [sceneName, setSceneName] = useState('My Scene');
  const [collectionName, setCollectionName] = useState('My Scene Collection');
  const [scenes, setScenes] = useState<CollectionScene[]>([]);
  const [selectedTpl, setSelectedTpl] = useState<Template | null>(null);

  // Load admin toggles on mount + poll for changes
  useEffect(() => {
    loadAdminToggles();
    const iv = setInterval(loadAdminToggles, 2000);
    return () => clearInterval(iv);
  }, [loadAdminToggles]);

  // Filter templates
  const filteredTemplates = useMemo(() => {
    return TEMPLATES.filter((tpl) => {
      if (adminToggles[tpl.id] === false) return false;
      const matchesCat = !selectedCategoryId || selectedCategoryId === 'all' || tpl.category === selectedCategoryId;
      const matchesSearch = !searchQuery || tpl.name.toLowerCase().includes(searchQuery.toLowerCase()) || tpl.category.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCat && matchesSearch;
    });
  }, [adminToggles, selectedCategoryId, searchQuery]);

  // Categories
  const categories: Category[] = useMemo(() => {
    const cats = new Map<string, { id: string; name: string; count: number }>();
    filteredTemplates.forEach((tpl) => {
      if (!cats.has(tpl.category)) {
        cats.set(tpl.category, { id: tpl.category, name: tpl.category, count: 0 });
      }
      cats.get(tpl.category)!.count++;
    });
    return [{ id: 'all', name: 'All Layouts', count: filteredTemplates.length }, ...Array.from(cats.values())];
  }, [filteredTemplates]);

  // Template selection
  const handleTemplateSelect = useCallback((template: Template) => {
    setSelectedTpl(template);
    setSources(templateToSources(template));
    setSceneName(template.name);
    setSelectedSceneId(`scene-${template.id}`);
    setIsVertical(template.vertical || false);
    setOpenSourceId(null);
    if (template.vertical && resolution.id !== 'vertical') {
      const vr = RESOLUTIONS.find((r) => r.id === 'vertical');
      if (vr) setResolution(vr);
    } else if (!template.vertical && resolution.id === 'vertical') {
      setResolution(RESOLUTIONS[0]);
    }
  }, [resolution, setSelectedSceneId, setIsVertical, setResolution]);

  // Source update
  const handleSourceUpdate = useCallback((index: number, updates: Partial<SceneSource>) => {
    setSources((prev) => {
      const n = [...prev];
      n[index] = { ...n[index], ...updates };
      return n;
    });
  }, []);

  const handleSourceRemove = useCallback((index: number) => {
    setSources((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Add scene to collection
  const addScene = useCallback(() => {
    if (!selectedTpl) return;
    const name = sceneName || selectedTpl.name;
    if (scenes.find((s) => s.name === name)) {
      showToast('Scene name already in collection');
      return;
    }
    setScenes((prev) => [...prev, { name, sources: sources.map((s) => ({ ...s })), vertical: !!selectedTpl.vertical }]);
    showToast('Scene added to collection');
  }, [selectedTpl, sceneName, sources, scenes, showToast]);

  // Export JSON
  const exportJSON = useCallback(() => {
    const all: Scene[] = scenes.length
      ? scenes.map((s, i) => ({ id: `scene-${i}`, name: s.name, sources: s.sources }))
      : selectedTpl
        ? [{ id: 'scene-0', name: sceneName || selectedTpl.name, sources }]
        : [];
    if (all.length === 0) { showToast('No scenes to export'); return; }
    const json = buildOBSProfile(all, collectionName, resolution);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
    a.download = collectionName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '') + '.json';
    a.click();
    showToast('JSON exported — import in OBS!');
  }, [scenes, selectedTpl, sceneName, sources, collectionName, resolution, showToast]);

  // Live JSON preview
  const liveJSON = useMemo(() => {
    if (!selectedTpl) return null;
    try {
      return buildOBSProfile(
        [{ id: 'preview', name: sceneName || selectedTpl.name, sources }],
        collectionName,
        resolution
      );
    } catch { return null; }
  }, [selectedTpl, sources, sceneName, collectionName, resolution]);

  const copyJSON = useCallback(() => {
    if (!liveJSON) return;
    copyToClipboard(liveJSON).then(() => showToast('Copied to clipboard!'));
  }, [liveJSON, showToast]);

  const sorted = useMemo(() => [...sources].sort((a, b) => a.order - b.order), [sources]);
  const jsonLines = liveJSON ? liveJSON.split('\n').slice(0, 55) : [];

  return (
    <div className="app">
      {/* LEFT */}
      <div className="left-col">
        <div className="search-wrap">
          <div className="search-icon">{ICONS.search}</div>
          <input className="search-box" placeholder="Search layouts..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
        <div className="cat-section">
          <div className="sec-head">{ICONS.grid}<span>Categories</span></div>
          <CategoryList categories={categories} selectedId={selectedCategoryId} onSelect={setSelectedCategoryId} />
        </div>
        <div className="layouts-section">
          <div className="sec-head">{ICONS.list}<span>Layouts</span></div>
          <div className="layouts-scroll">
            {filteredTemplates.length === 0 ? (
              <div className="no-results">No layouts match</div>
            ) : (
              filteredTemplates.map((tpl) => (
                <LayoutCard
                  key={tpl.id}
                  scene={{ id: `scene-${tpl.id}`, name: tpl.name, sources: tpl.sources.map((s, i) => ({ id: `src-${i}`, type: s.type as SceneSource['type'], slot: s.slot, name: s.slot, position: s.position, size: s.size, order: s.order, visible: true, border: s.border ? { ...s.border } : undefined })) }}
                  resolution={resolution}
                  isActive={selectedSceneId === `scene-${tpl.id}`}
                  isVertical={tpl.vertical || false}
                  onClick={() => handleTemplateSelect(tpl)}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* MIDDLE */}
      <div className="mid-col">
        <div className="mid-header">
          <span style={{ color: 'var(--teal)', display: 'flex' }}>{ICONS.sources}</span>
          <span className="mid-title">Sources</span>
        </div>
        <div className="mid-body">
          {!selectedTpl ? (
            <div className="no-scene">← Select a layout to begin</div>
          ) : (
            <>
              <div className="field-group">
                <label className="field-label">Scene Name</label>
                <input className="field-input" value={sceneName} onChange={(e) => setSceneName(e.target.value)} placeholder="My Scene" />
              </div>
              <div className="field-group">
                <label className="field-label">Resolution</label>
                <select className="field-select" value={resolution.id} onChange={(e) => { const r = RESOLUTIONS.find((x) => x.id === e.target.value); if (r) setResolution(r); }}>
                  {RESOLUTIONS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>
              <label className="src-list-lbl">Source List</label>
              <div className="src-cards">
                {sorted.map((src, i) => (
                  <SourceCard key={src.id} source={src} isOpen={openSourceId === src.id} onToggle={() => setOpenSourceId(openSourceId === src.id ? null : src.id)} onUpdate={(u) => handleSourceUpdate(i, u)} onRemove={() => handleSourceRemove(i)} />
                ))}
              </div>
              <div className="action-row" style={{ marginTop: '14px' }}>
                <button className="btn btn-ghost" style={{ flex: 1 }} onClick={addScene}>{ICONS.plus} Add to Collection</button>
                <button className="btn btn-teal" style={{ flex: 1 }} onClick={exportJSON}>{ICONS.download} Export JSON</button>
              </div>
              <div className="coll-group">
                <label className="field-label">Collection Name</label>
                <input className="field-input" value={collectionName} onChange={(e) => setCollectionName(e.target.value)} placeholder="My Scene Collection" />
              </div>
            </>
          )}
        </div>
        <div className="queue-bar">
          <div className="queue-row">
            <span className="queue-lbl">Collection{scenes.length ? ` (${scenes.length})` : ''}</span>
            {scenes.length === 0 ? (
              <span className="queue-empty-txt">No scenes added yet</span>
            ) : (
              scenes.map((s) => (
                <div key={s.name} className={`q-chip${s.vertical ? ' v-chip' : ''}`}>
                  {s.name.length > 20 ? s.name.slice(0, 18) + '…' : s.name}
                  <button className="q-x" onClick={() => setScenes((p) => p.filter((x) => x.name !== s.name))}>×</button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* RIGHT */}
      <div className="right-col">
        <div className="right-scroll">
          {/* Preview */}
          <div className="panel-card">
            <div className="panel-head">
              <div className="panel-head-title"><span className="ph-icon">{ICONS.preview}</span>Live Preview</div>
              <div className="panel-head-right">
                {isVertical && <span className="res-badge vert-res-badge">9:16</span>}
                <span className="res-badge">{resolution.w}×{resolution.h}</span>
              </div>
            </div>
            <CanvasPreview sources={sources} resolution={resolution} isVertical={isVertical} />
            {selectedTpl && <div className="preview-info"><strong>{sceneName}</strong> · {sources.length} source{sources.length !== 1 ? 's' : ''} configured</div>}
          </div>

          {/* JSON Preview */}
          <div className="panel-card">
            <div className="panel-head">
              <div className="panel-head-title"><span className="ph-icon">{ICONS.code}</span>Export Preview</div>
              <div className="json-actions" style={{ display: 'flex', gap: '6px' }}>
                <button className="btn btn-ghost btn-sm" onClick={copyJSON} disabled={!liveJSON}>{ICONS.copy} Copy</button>
                <button className="btn btn-teal btn-sm" onClick={exportJSON} disabled={!selectedTpl && !scenes.length}>{ICONS.download} Download</button>
              </div>
            </div>
            {liveJSON ? (
              <div className="json-pre">{jsonLines.map((line, i) => <div key={i}>{line}</div>)}</div>
            ) : (
              <div className="json-empty">// Select a layout to see JSON output</div>
            )}
          </div>

          {/* Instructions */}
          <div className="panel-card">
            <div className="panel-head">
              <div className="panel-head-title"><span className="ph-icon">{ICONS.info}</span>Import Instructions</div>
            </div>
            <div className="panel-body">
              <div className="import-steps">
                {['Open OBS Studio (28.0 or later)', 'Scene Collection → Import → select .json file', 'Switch to your new scene collection', 'Right-click each source → Properties to assign devices', 'Vertical templates: set OBS canvas to 1080×1920 first'].map((txt, i) => (
                  <div key={i} className="imp-step"><div className="imp-n">{i + 1}</div><div className="imp-t">{txt}</div></div>
                ))}
              </div>
              <div className="imp-tip">
                <div className="imp-tip-title">
                  <span>💡</span> Avoid Device Resets in OBS
                </div>
                <div className="imp-tip-body">
                  Importing a new collection in OBS resets device inputs. To avoid setting up cameras/mics every time:
                  <ul style={{ paddingLeft: '16px', marginTop: '5px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <li><strong>Use Collections:</strong> Queue all scenes under the middle panel ("Add to Collection") and export them together. Shared sources like "Camera" only need to be configured once!</li>
                    <li><strong>Source Copy:</strong> Install the OBS "Source Copy" plugin to copy-paste new scenes directly into your existing configured collection.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {ToastComponent}
    </div>
  );
}

export default MainApp;
